/**
 * KapiForm.tsx — Zod-powered form system
 *
 * Provides:
 * - KapiForm: wrapper that manages submit state + error display
 * - Field: label + input wrapper with inline error
 * - useKapiForm: hook that integrates Zod validation
 * - Input/Select/Textarea primitives with consistent styling
 *
 * Features:
 * - Zod validation on submit (no external form library dep)
 * - Inline field errors, cleared on change
 * - Loading state disables all fields
 * - Touch targets ≥44px for all interactive elements
 * - Accessible: aria-invalid, aria-describedby on errors
 * - Dark mode via CSS vars
 */

import {
  useState,
  useCallback,
  createContext,
  useContext,
  type ReactNode,
  type FormEvent,
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import type { ZodSchema, ZodError } from 'zod';

// ---------------------------------------------------------------------------
// Form context
// ---------------------------------------------------------------------------

interface FormContextValue {
  /** Whether form is submitting */
  isSubmitting: boolean;
  /** Field-level errors keyed by field name */
  errors: Record<string, string>;
  /** Clear a specific field error */
  clearError: (name: string) => void;
}

const FormContext = createContext<FormContextValue>({
  isSubmitting: false,
  errors: {},
  clearError: () => {},
});

const useFormContext = (): FormContextValue => useContext(FormContext);

// ---------------------------------------------------------------------------
// useKapiForm hook
// ---------------------------------------------------------------------------

export interface UseKapiFormOptions<T extends Record<string, unknown>> {
  /** Initial field values */
  defaultValues: T;
  /** Zod schema for validation */
  schema?: ZodSchema<T>;
  /** Called with validated data on submit */
  onSubmit: (data: T) => void | Promise<void>;
}

export interface UseKapiFormReturn<T extends Record<string, unknown>> {
  values: T;
  errors: Record<string, string>;
  isSubmitting: boolean;
  setValue: <K extends keyof T>(name: K, value: T[K]) => void;
  setValues: (partial: Partial<T>) => void;
  reset: () => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
  clearError: (name: string) => void;
  /** True when all required fields have values (basic check, not full validation) */
  isDirty: boolean;
}

/**
 * useKapiForm — lightweight form state manager with Zod validation
 *
 * @example
 * ```tsx
 * const form = useKapiForm({
 *   defaultValues: { name: '', price: 0 },
 *   schema: productSchema,
 *   onSubmit: async (data) => await createProduct(data),
 * });
 * ```
 */
export function useKapiForm<T extends Record<string, unknown>>({
  defaultValues,
  schema,
  onSubmit,
}: UseKapiFormOptions<T>): UseKapiFormReturn<T> {
  const [values, setValuesState] = useState<T>(defaultValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setValue = useCallback(<K extends keyof T>(name: K, value: T[K]) => {
    setValuesState((prev) => ({ ...prev, [name]: value }));
    // Clear error when field changes
    setErrors((prev) => {
      if (!(name as string in prev)) return prev;
      const next = { ...prev };
      delete next[name as string];
      return next;
    });
  }, []);

  const setValues = useCallback((partial: Partial<T>) => {
    setValuesState((prev) => ({ ...prev, ...partial }));
  }, []);

  const reset = useCallback(() => {
    setValuesState(defaultValues);
    setErrors({});
  }, [defaultValues]);

  const clearError = useCallback((name: string) => {
    setErrors((prev) => {
      if (!(name in prev)) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setErrors({});

      let parsed: T = values;

      if (schema) {
        const result = schema.safeParse(values);
        if (!result.success) {
          const fieldErrors: Record<string, string> = {};
          for (const issue of (result.error as ZodError).issues) {
            const key = issue.path.join('.');
            if (key) fieldErrors[key] = issue.message;
          }
          setErrors(fieldErrors);
          return;
        }
        parsed = result.data;
      }

      setIsSubmitting(true);
      Promise.resolve(onSubmit(parsed)).finally(() => setIsSubmitting(false));
    },
    [values, schema, onSubmit],
  );

  const isDirty = JSON.stringify(values) !== JSON.stringify(defaultValues);

  return {
    values,
    errors,
    isSubmitting,
    setValue,
    setValues,
    reset,
    handleSubmit,
    clearError,
    isDirty,
  };
}

// ---------------------------------------------------------------------------
// KapiForm wrapper
// ---------------------------------------------------------------------------

export interface KapiFormProps {
  children: ReactNode;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  errors?: Record<string, string>;
  clearError?: (name: string) => void;
  isSubmitting?: boolean;
  className?: string;
  /** Top-level error (e.g., API error) */
  formError?: string | null;
  id?: string;
  /** aria-label for the form */
  ariaLabel?: string;
}

/**
 * KapiForm — form wrapper that provides context to Field children
 */
export function KapiForm({
  children,
  onSubmit,
  errors = {},
  clearError = () => {},
  isSubmitting = false,
  className = '',
  formError,
  id,
  ariaLabel,
}: KapiFormProps): React.ReactElement {
  return (
    <FormContext.Provider value={{ isSubmitting, errors, clearError }}>
      <form
        id={id}
        onSubmit={onSubmit}
        className={`flex flex-col gap-4 ${className}`}
        aria-label={ariaLabel}
        noValidate
      >
        {formError && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-4 py-3
                       text-sm text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-400"
          >
            {formError}
          </div>
        )}
        {children}
      </form>
    </FormContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Field wrapper
// ---------------------------------------------------------------------------

export interface FieldProps {
  /** Field name — used to look up errors in context */
  name: string;
  /** Label text */
  label: string;
  /** Whether field is required (shows asterisk) */
  required?: boolean;
  /** Helper text below the field */
  hint?: string;
  /** Inline override of error message */
  error?: string;
  children: ReactNode;
  /** Additional CSS class */
  className?: string;
}

/**
 * Field — label + input + error wrapper.
 * Wires up aria-describedby automatically.
 *
 * @example
 * ```tsx
 * <Field name="email" label="Correo" required>
 *   <FormInput name="email" type="email" value={form.values.email}
 *              onChange={(e) => form.setValue('email', e.target.value)} />
 * </Field>
 * ```
 */
export function Field({
  name,
  label,
  required = false,
  hint,
  error: errorOverride,
  children,
  className = '',
}: FieldProps): React.ReactElement {
  const { errors } = useFormContext();
  const errorMsg = errorOverride ?? errors[name];
  const errorId = `${name}-error`;
  const hintId = `${name}-hint`;

  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      <label
        htmlFor={name}
        className="text-sm font-medium text-text-primary"
      >
        {label}
        {required && (
          <span className="ml-0.5 text-red-500" aria-hidden="true">*</span>
        )}
      </label>
      {hint && (
        <p id={hintId} className="text-xs text-text-secondary">
          {hint}
        </p>
      )}
      {/* Clone children to inject aria attrs if possible */}
      {children}
      {errorMsg && (
        <p id={errorId} role="alert" className="text-xs text-red-600 dark:text-red-400">
          {errorMsg}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FormInput
// ---------------------------------------------------------------------------

export interface FormInputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Field name (must match Field parent name) */
  name: string;
  /** Override error from context */
  hasError?: boolean;
}

/**
 * FormInput — styled input that reads error state from FormContext.
 * Use inside a `Field` component.
 */
export function FormInput({ name, hasError: hasErrorProp, className = '', ...rest }: FormInputProps): React.ReactElement {
  const { errors, isSubmitting, clearError } = useFormContext();
  const hasError = hasErrorProp ?? Boolean(errors[name]);
  const errorId = `${name}-error`;

  return (
    <input
      id={name}
      name={name}
      disabled={isSubmitting || rest.disabled}
      aria-invalid={hasError}
      aria-describedby={hasError ? errorId : undefined}
      className={[
        'kapi-input',
        hasError ? 'border-red-400 focus:ring-red-400' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      onChange={(e) => {
        clearError(name);
        rest.onChange?.(e);
      }}
      {...rest}
    />
  );
}

// ---------------------------------------------------------------------------
// FormSelect
// ---------------------------------------------------------------------------

export interface FormSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  name: string;
  hasError?: boolean;
  /** Options list */
  options: Array<{ value: string | number; label: string; disabled?: boolean }>;
  /** Placeholder option */
  placeholder?: string;
}

/**
 * FormSelect — styled select element with error awareness.
 * Use inside a `Field` component.
 */
export function FormSelect({
  name,
  hasError: hasErrorProp,
  options,
  placeholder,
  className = '',
  ...rest
}: FormSelectProps): React.ReactElement {
  const { errors, isSubmitting, clearError } = useFormContext();
  const hasError = hasErrorProp ?? Boolean(errors[name]);
  const errorId = `${name}-error`;

  return (
    <select
      id={name}
      name={name}
      disabled={isSubmitting || rest.disabled}
      aria-invalid={hasError}
      aria-describedby={hasError ? errorId : undefined}
      className={[
        'kapi-input pr-8',
        hasError ? 'border-red-400 focus:ring-red-400' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      onChange={(e) => {
        clearError(name);
        rest.onChange?.(e);
      }}
      {...rest}
    >
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value} disabled={opt.disabled}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

// ---------------------------------------------------------------------------
// FormTextarea
// ---------------------------------------------------------------------------

export interface FormTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  name: string;
  hasError?: boolean;
}

/**
 * FormTextarea — styled textarea with error awareness.
 * Use inside a `Field` component.
 */
export function FormTextarea({
  name,
  hasError: hasErrorProp,
  className = '',
  ...rest
}: FormTextareaProps): React.ReactElement {
  const { errors, isSubmitting, clearError } = useFormContext();
  const hasError = hasErrorProp ?? Boolean(errors[name]);
  const errorId = `${name}-error`;

  return (
    <textarea
      id={name}
      name={name}
      rows={4}
      disabled={isSubmitting || rest.disabled}
      aria-invalid={hasError}
      aria-describedby={hasError ? errorId : undefined}
      className={[
        'kapi-input resize-y',
        hasError ? 'border-red-400 focus:ring-red-400' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      onChange={(e) => {
        clearError(name);
        rest.onChange?.(e);
      }}
      {...rest}
    />
  );
}

// ---------------------------------------------------------------------------
// SubmitButton
// ---------------------------------------------------------------------------

export interface SubmitButtonProps {
  /** Button label when idle */
  label: string;
  /** Button label when submitting */
  loadingLabel?: string;
  /** Additional classes */
  className?: string;
  /** Whether to show loading spinner */
  disabled?: boolean;
}

/**
 * SubmitButton — disables and shows spinner during form submission.
 * Reads isSubmitting from FormContext.
 */
export function SubmitButton({
  label,
  loadingLabel = 'Guardando…',
  className = '',
  disabled: disabledProp = false,
}: SubmitButtonProps): React.ReactElement {
  const { isSubmitting } = useFormContext();
  const disabled = disabledProp || isSubmitting;

  return (
    <button
      type="submit"
      disabled={disabled}
      className={[
        'kapi-btn flex items-center justify-center gap-2',
        disabled ? 'opacity-60 cursor-not-allowed' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      aria-busy={isSubmitting}
    >
      {isSubmitting && (
        <svg
          className="h-4 w-4 animate-spin"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
      )}
      {isSubmitting ? loadingLabel : label}
    </button>
  );
}
